import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Badge, Form, InputGroup, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchProgramsUnified, fetchUpcomingPrograms } from "../services/ProgramService";
import { fetchLanguages } from "../services/LocationService";
import LanguageData from "../models/LanguageData";
import ProgramData from "../models/ProgramData";
import { IMAGE_BASE } from "../Config";
import { getPlayImageUrl, handleImageError } from "../utils/imageUtils";

const PlaysPage: React.FC = () => {
  const navigate = useNavigate();
  const [plays, setPlays] = useState<ProgramData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [upcomingPlays, setUpcomingPlays] = useState<ProgramData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [languages, setLanguages] = useState<LanguageData[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Use ref to track if initial load has happened
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadLanguages();
      performSearch(true);
    }
  }, []);

  // Trigger search when language changes
  useEffect(() => {
    if (hasLoadedRef.current) {
      setPage(1);
      performSearch(true);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    loadUpcomingPlays();
  }, []);

  const loadLanguages = async () => {
    try {
      const languagesData = await fetchLanguages();
      setLanguages(languagesData);
    } catch (error) {
      setError("Failed to load languages");
    }
  };

  const loadUpcomingPlays = async () => {
    try {
      const response = await fetchUpcomingPrograms(8);
      setUpcomingPlays(response.programs || []);
    } catch (error) {
      setError("Failed to load upcoming plays");
    }
  };

  const performSearch = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentPage = reset ? 1 : page;
      const searchText = searchQuery.trim();
      const languageCode = selectedLanguage;
      
      const response = await fetchProgramsUnified(
        currentPage,
        12,
        searchText,
        languageCode,
        ""
      );
      
      if (reset) {
        setPlays(response.programs || []);
        setPage(2);
        setTotalResults(response.totalPrograms || 0);
      } else {
        setPlays(prev => [...prev, ...(response.programs || [])]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(response.hasMore || false);
    } catch (err) {
      setError("Failed to load plays");
      if (reset) {
        setPlays([]);
        setTotalResults(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClick = () => {
    setPage(1);
    performSearch(true);
  };

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
  };



  const handleLoadMore = () => {
    if (!loading && hasMore) {
      performSearch(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedLanguage("");
    setPage(1);
    setTimeout(() => {
      performSearch(true);
    }, 50);
  };

  const handlePlayClick = (id: string) => {
    navigate(`/play/${id}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'upcoming': return 'success';
      case 'coming-soon': return 'warning';
      case 'new': return 'info';
      default: return 'secondary';
    }
  };

  const getSelectedLanguageName = () => {
    if (!selectedLanguage) return "";
    const lang = languages.find(l => l.code === selectedLanguage);
    return lang?.nativeName || lang?.name || selectedLanguage;
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)' }}
            >
              <i className="bi bi-play-circle-fill text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#810081' }}>Theater Plays</h1>
              <p className="text-muted mb-0">Discover amazing theatrical productions</p>
            </div>
          </div>
        </div>

        {/* Modern Upcoming Shows */}
        {upcomingPlays.length > 0 && (
          <Card className="border-0 shadow-sm mb-5" style={{ borderRadius: '20px' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-4">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: '40px', height: '40px', backgroundColor: '#FFD600' }}
                >
                  <i className="bi bi-calendar-event" style={{ color: '#810081' }}></i>
                </div>
                <h3 className="fw-bold mb-0" style={{ color: '#810081' }}>Upcoming Shows</h3>
              </div>
              <div className="d-flex overflow-auto pb-3" style={{ gap: "1rem" }}>
                {upcomingPlays.map((play) => (
                  <Card 
                    key={play._id}
                    className="flex-shrink-0 border-0" 
                    onClick={() => handlePlayClick(play._id)}
                    style={{ 
                      cursor: "pointer", 
                      width: "280px",
                      borderRadius: '15px',
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: '0 4px 20px rgba(129, 0, 129, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                      e.currentTarget.style.boxShadow = "0 20px 40px rgba(129, 0, 129, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow = "0 4px 20px rgba(129, 0, 129, 0.1)";
                    }}
                  >
                    <div className="position-relative overflow-hidden" style={{ borderRadius: '15px 15px 0 0' }}>
                      <Card.Img 
                        variant="top" 
                        src={getPlayImageUrl(play.image1)} 
                        style={{ height: "160px", objectFit: "cover" }}
                        onError={(e) => handleImageError(e, 'PLAY')}
                      />
                      <span 
                        className="badge position-absolute top-0 start-0 m-2"
                        style={{ backgroundColor: '#28a745', borderRadius: '50px', padding: '6px 12px' }}
                      >
                        UPCOMING
                      </span>
                    </div>
                    <Card.Body className="p-3">
                      <Card.Title className="fw-bold mb-2" style={{ fontSize: "1rem", color: '#810081' }}>
                        {play.title}
                      </Card.Title>
                      <Card.Text className="text-muted" style={{ fontSize: "0.85rem", lineHeight: "1.4" }}>
                        {play.description && play.description.length > 80 
                          ? `${play.description.substring(0, 80)}...` 
                          : play.description || "An exciting upcoming show."}
                      </Card.Text>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted">
                          <i className="bi bi-clock me-1"></i>
                          {formatDuration(play.durationHours, play.durationMinutes)}
                        </small>
                        <small style={{ color: '#FFD600', fontWeight: '600' }}>
                          View Details
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Modern Search Section */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col lg={8}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search plays by title, description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="ps-5 py-3 border-0 bg-light"
                    style={{ borderRadius: '50px', fontSize: '16px' }}
                  />
                  <i 
                    className="bi bi-search position-absolute" 
                    style={{ left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#810081' }}
                  ></i>
                  <Button 
                    onClick={handleSearchClick} 
                    disabled={loading}
                    className="position-absolute border-0"
                    style={{ 
                      right: '8px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: '#FFD600',
                      color: '#810081',
                      borderRadius: '50px',
                      width: '40px',
                      height: '40px'
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <i className="bi bi-search"></i>}
                  </Button>
                </div>
              </Col>
              <Col lg={4}>
                <Form.Select 
                  value={selectedLanguage} 
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  disabled={loading}
                  className="py-3 border-0 bg-light"
                  style={{ borderRadius: '50px', fontSize: '16px' }}
                >
                  <option value="">All Languages</option>
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.nativeName || lang.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            
            {/* Active Filters Pills */}
            {(searchQuery || selectedLanguage) && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                <small className="text-muted me-2">Active filters:</small>
                {searchQuery && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    Search: "{searchQuery}"
                    <button 
                      className="btn-close btn-close-white ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => {
                        setSearchQuery("");
                        setPage(1);
                        setTimeout(() => performSearch(true), 50);
                      }}
                    ></button>
                  </span>
                )}
                {selectedLanguage && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#FFD600', color: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    Language: {getSelectedLanguageName()}
                    <button 
                      className="btn-close ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => {
                        setSelectedLanguage("");
                      }}
                    ></button>
                  </span>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modern Results Header */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#810081' }}>
                {totalResults > 0 ? `${totalResults} Plays Found` : "No Plays Found"}
              </h5>
              <small className="text-muted">
                {searchQuery && `Searching for "${searchQuery}"`}
                {selectedLanguage && ` in ${getSelectedLanguageName()}`}
              </small>
            </div>
            {totalResults > 0 && (
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="rounded-pill"
                onClick={() => {
                  const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                  setSortOrder(newOrder);
                  const sortedPlays = [...plays].sort((a, b) => {
                    return newOrder === 'asc' 
                      ? a.title.localeCompare(b.title)
                      : b.title.localeCompare(a.title);
                  });
                  setPlays(sortedPlays);
                }}
              >
                <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
                Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )}
          </div>
        )}

        {/* Modern Error Message */}
        {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              color: '#810081',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#FFD600' }}></i>
            {error}
          </Alert>
        )}

      {/* Plays Grid */}
      <Row className="g-4">
        {plays.map((play) => (
          <Col key={play._id} lg={4} md={6}>
            <Card 
              className="h-100 shadow-sm border-0" 
              onClick={() => handlePlayClick(play._id)}
              style={{ cursor: "pointer", transition: "all 0.3s ease" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
              }}
            >
              <div className="position-relative">
                <Card.Img 
                  variant="top" 
                  src={getPlayImageUrl(play.image1)} 
                  style={{ height: "250px", objectFit: "cover" }}
                  onError={(e) => handleImageError(e, 'PLAY')}
                />
                <div className="position-absolute top-0 start-0 m-2">
                  <Badge bg={getStatusColor(play.status)} className="px-2 py-1">
                    {play.status === 'coming-soon' ? 'COMING SOON' : 
                     play.status === 'new' ? 'NEW' :
                     play.status?.toUpperCase() || 'NEW'}
                  </Badge>
                </div>
                {play.isFeatured && (
                  <div className="position-absolute top-0 end-0 m-2">
                    <Badge bg="warning" text="dark">
                      <i className="bi bi-star-fill me-1"></i>Featured
                    </Badge>
                  </div>
                )}
              </div>
              <Card.Body className="d-flex flex-column">
                <Card.Title className="fw-bold mb-2">{play.title}</Card.Title>
                <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                  {play.description && play.description.length > 100 
                    ? `${play.description.substring(0, 100)}...` 
                    : play.description || "An exciting theatrical production awaiting your discovery."}
                </Card.Text>
                <div className="mt-auto">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      <i className="bi bi-clock me-1"></i>
                      {formatDuration(play.durationHours, play.durationMinutes)}
                    </small>
                    {play.language && (
                      <Badge bg="outline-info" text="dark">
                        {languages.find(l => l.code === play.language)?.nativeName || 
                         languages.find(l => l.code === play.language)?.name || 
                         play.language}
                      </Badge>
                    )}
                  </div>
                  {play.director && (
                    <small className="text-muted d-block mb-2">
                      <i className="bi bi-person-badge me-1"></i>
                      Director: {play.director}
                    </small>
                  )}
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-warning fw-bold">
                      View Details <i className="bi bi-arrow-right"></i>
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

        {/* Modern Load More Button */}
        {hasMore && plays.length > 0 && (
          <div className="text-center mt-5">
            <Button 
              onClick={handleLoadMore} 
              disabled={loading}
              className="rounded-pill border-0 px-5 py-3"
              style={{ 
                background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 15px rgba(129, 0, 129, 0.3)'
              }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading More...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Load More Plays
                </>
              )}
            </Button>
          </div>
        )}

        {/* Modern Empty State */}
        {!loading && plays.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, rgba(255, 214, 0, 0.2) 0%, rgba(129, 0, 129, 0.2) 100%)' 
                }}
              >
                <i className="bi bi-play-circle" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#810081' }}>No Theater Plays Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {searchQuery || selectedLanguage
                  ? "We couldn't find any plays matching your criteria. Try adjusting your search or filters." 
                  : "Be the first to discover amazing theatrical productions!"}
              </p>
              {(searchQuery || selectedLanguage) && (
                <Button 
                  onClick={handleClearFilters}
                  disabled={loading}
                  className="rounded-pill border-0 px-4 py-2"
                  style={{ backgroundColor: '#FFD600', color: '#810081', fontWeight: '600' }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Clear All Filters
                </Button>
              )}
            </Card.Body>
          </Card>
        )}

      {/* Loading State */}
      {loading && plays.length === 0 && (
        <Row className="py-5">
          <Col xs={12} className="text-center">
            <Spinner animation="border" variant="warning" />
            <p className="mt-3">Searching for plays...</p>
          </Col>
        </Row>
      )}
      </Container>
    </div>
  );
};

export default PlaysPage;